import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { Select, Store } from '@ngxs/store';
import { DeleteProvider, Provider, ProviderStatus, ProviderUser, ProviderUserRole, ProviderUserStatus, SaveProvider, PractitionerGroupService, PractitionerGroupEntity, AlertService, CommonService, ServiceLevel, NetworkMember, ProviderSetting, GetProviderSettings } from 'huddyapi';
import { Observable, Subscription, timer } from 'rxjs';
import { AppComponent } from '../app.component';
import { SavedGroupStatus, SavedObject, SavedSelectField, SavedTextField, SelectOption, SelectOptions, SimpleMultisavingPage, CountUpdators, MapCountDependency, SimpleStatusField, SumContributionsDependency } from '../components/multisave';
import { Util } from '../components/util';
import { DesktopErrorHandler } from '../services/errorHandler.service';


@Component({
  selector: 'app-providers',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class ProvidersComponent extends SimpleMultisavingPage<SavedProvider, Provider> implements OnInit, OnDestroy {
  @Select(state => state.compassState.providers) providers$: Observable<Provider[]>;
  @Select(state => state.compassState.providerSettings) providerSettings$: Observable<ProviderSetting[]>;
  @ViewChild('infoButton') infoButton: MatButton;
  @ViewChild('popover') popover: ElementRef;

  providerSubscription: Subscription;
  providerSettingsSubscription;
  providerSettings : ProviderSetting[] = [];
  isMottHaven : boolean = false;
  nameDependency: MapCountDependency<string>;
  shouldScrollToNew: boolean;
  infoPopoverShowing: boolean;
  infoPopoverShown: boolean;
  infoPopoverPosition: string;
  selectedTab: string = 'general';

  constructor(
    public errorHandler: DesktopErrorHandler,
    public store: Store,
    public snackbar: MatSnackBar,
    public dialog: MatDialog,
    public practitionerGroupService: PractitionerGroupService,
    public alertService : AlertService
  ) {
    super('provider', 'providers', errorHandler, store, snackbar, dialog);
    this.infoPopoverShowing = false;
    this.infoPopoverShown = false;
    this.infoPopoverPosition = '';
    this.nameDependency = new MapCountDependency<string>();
    this.shouldScrollToNew = false;
  }

  ngOnInit(): void {
    this.providerSubscription = this.providers$.subscribe((providers: Provider[]) => this.updateObjectListAndMap(providers));
    this.providerSettingsSubscription = this.providerSettings$.subscribe((providerSettings: ProviderSetting[]) => {
      this.providerSettings = providerSettings;
      this.isMottHaven = providerSettings.filter(p => p.name == 'mottHaven' && p.value == 'true').length > 0;
    });
  }


  ngOnDestroy(): void {
    if(this.providerSubscription)
      this.providerSubscription.unsubscribe();
    if(this.providerSettingsSubscription)
      this.providerSettingsSubscription.unsubscribe();
  }

  isUserPRSAdmin() : boolean {
  
    return CommonService.getUser().authorities.indexOf("ROLE_PRS_ADMIN") > -1;
  }

  canCleanProvider() : boolean {
    return CommonService.getUser().authorities.indexOf("ROLE_TEST_ADMIN") > -1;
  }


  afterContentChecked(): void {}

  constructNewSavedObject(from: Provider): SavedProvider {
    return new SavedProvider(from, this);
  }

  compareSavedObjects(a: SavedProvider, b: SavedProvider): number {
    return SavedProvider.compare(a, b);
  }

  ignoreKeyboardArrowEvent(event: any): boolean {
    return event.target.tagName == 'MAT-SELECT' || event.target.tagName == 'TEXTAREA';
  }

  selectionChanged(): void {
    console.log('selection changed', this.selectedId);
    if (this.selectedId) {
      this.store.dispatch(new GetProviderSettings(this.selectedId));
    }
  }

  scrollToNew(element: ElementRef) {
    if(!this.shouldScrollToNew)
      return;
    this.shouldScrollToNew = false;
    timer(0).subscribe(() => {
      element?.nativeElement?.scrollIntoViewIfNeeded();
    });
  }

  setShouldScrollToNew(): void {
    this.shouldScrollToNew = true;
  }

  isLoading(): boolean {
    return AppComponent.loading;
  }


  isCommunity() : boolean {
    return AppComponent.product === 'COMMUNITY';
  }

  getInfoPopoverPosition() {
    let top: number = 0, left: number = 0;
    if(this.infoPopoverShown && this.popover && this.popover.nativeElement
      && this.infoButton && this.infoButton._elementRef && this.infoButton._elementRef.nativeElement) {
      const position = this.infoButton._elementRef.nativeElement.getBoundingClientRect();
      const bodyPosition = this.popover.nativeElement.parentElement.getBoundingClientRect();
      top = position.y - bodyPosition.y + 28;
      left = position.x - bodyPosition.x - 25;
    }
    return 'top:' + top + 'px;left:' + left + 'px;';
  }

  showInfoPopover() {
    if(!this.infoPopoverShown)
      this.infoPopoverShown = true;
    this.infoPopoverShowing = true;
  }

  hideInfoPopover() {
    this.infoPopoverShowing = false;
  }
}

export class SavedProvider extends SavedObject<Provider> {
  objectName = 'provider';
  parent: ProvidersComponent;
  name: SavedTextField;
  private: SavedSelectField<boolean>;
  description: SavedTextField;
  emailDependency: MapCountDependency<string>;
  seatCount: number;
  seatDependency: SumContributionsDependency;
  users: ProviderUserGroup[];
  savedUsers: ProviderUser[];
  userStatusField: SimpleStatusField;
  usersGroup: SavedGroupStatus;


  constructor(from: Provider, parent: ProvidersComponent) {
    super(from, false, 0, parent.updators, parent.dialog);
    this.parent = parent;
    this.emailDependency = new MapCountDependency<string>();
    this.seatDependency = new SumContributionsDependency();
    this.seatCount = from?.seatCount || 0;
    this.users = [];
    this.savedUsers = from?.users || [];
    this.usersGroup = new SavedGroupStatus(this.updators);
    this.userStatusField = new SimpleStatusField(this.usersGroup.updators, [this.seatDependency], [
      (): string => this.seatDependency.getSum() > this.seatCount ? 'Too many active users' : null
    ], (): boolean =>
      this.users.length != this.savedUsers.length || this.users.findIndex((user: ProviderUserGroup): boolean => user.isNew) != -1);
    this.userStatusField.touch();
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, [parent.nameDependency],
      [(field: SavedTextField): string => parent.nameDependency.count(field.value) > 1 ? 'Duplicate' : null], null, (oldValue: string, newValue: string): void => {
        if(oldValue != null && newValue != null)
          parent.nameDependency.replace(oldValue, newValue);
        else if(oldValue != null)
          parent.nameDependency.subtract(oldValue);
        else if(newValue != null)
          parent.nameDependency.add(newValue);
      }, true, null, 128
    );
    this.private = new SavedSelectField<boolean>(from ? from.status == ProviderStatus.PRIVATE_AND_PROTECTED_ONLY : true, null,
      this.updators, null, null, true, null, null, new SelectOptions<boolean>(
        [{ value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }],
        (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)
      )
    );
    this.description = new SavedTextField(from ? from.description : null, null, this.updators, null, null, null, null,
      true, null, 1024);

    this.setUsers(this.savedUsers);
  }

  private clearUsers(shouldUpdate: boolean = true) {
    this.users.forEach((user: ProviderUserGroup) => user.destroy());
    this.users = [];
    if(shouldUpdate)
      this.userStatusField.update();
  }

  private setUsers(providerUsers?: ProviderUser[], forceNew: boolean = false) {
    if(providerUsers?.length > 0) {
      if(forceNew) {
        this.clearUsers(false);
        providerUsers.forEach((providerUser: ProviderUser) => this.addUser(providerUser, false, true));
        this.userStatusField.update();
        return;
      }
      let i = 0;
      const minLength: number = Math.min(providerUsers.length, this.users.length);
      while(i < minLength) {
        this.users[i].updateFrom(providerUsers[i]);
        i++;
      }
      while(i < providerUsers.length) {
        this.addUser(providerUsers[i], false);
        i++;
      }
      while(i < this.users.length)
        this.removeUser(i, false);
    } else
      this.clearUsers(false);
    this.userStatusField.update();
  }

  getSavedName(): string {
    return this.name.savedValue;
  }

  beforeGroupChanged() {}

  onGroupChanged(): void {
    this.parent.shouldAutofocus = true;
    this.parent.fieldInfoElem = null;
  }

  onUpdateFrom(from: Provider, wasNew: boolean) {
    this.name.savedValue = from ? from.name : null;
    this.private.savedValue = from ? from.status == ProviderStatus.PRIVATE_AND_PROTECTED_ONLY : true;
    this.description.savedValue = from ? from.description : null;
    this.savedUsers = from?.users || [];
    if(wasNew)
      this.setUsers(this.savedUsers);
    else {
      this.users.forEach((user: ProviderUserGroup, index: number) => user.updateFrom(this.savedUsers[index]));
      this.userStatusField.update();
    }
  }

  touchAll(value: boolean): void {
    this.userStatusField.touch();
    this.name.touch(value);
    this.private.touch(value);
    this.description.touch(value);
    this.users.forEach((user: ProviderUserGroup) => user.touchAll(value));
  }

  warnValidation(): void {
    this.parent.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
  }

  onReset(): void {
    this.name.reset();
    this.private.reset();
    this.description.reset();
    this.setUsers(this.savedUsers);
    this.users.forEach((user: ProviderUserGroup) => user.resetAll());
    this.userStatusField.reset();
    this.userStatusField.update();
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new SaveProvider(new Provider({
      id: this.id,
      name: this.name.value,
      status: this.private.value ? ProviderStatus.PRIVATE_AND_PROTECTED_ONLY : ProviderStatus.PENDING,
      description: this.description.value,
      users: this.users.map((user: ProviderUserGroup): ProviderUser => user.getSaved())
    }))).toPromise().then(() => true).catch((error: any) => {
      if(error && error.name == 'taken')
        this.name.error = 'This name is taken';
      else if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, 'provider');
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new DeleteProvider(this.id)).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue);
      return false;
    });
  }

  onDeleteNew(): void {
    this.parent.removeSavedObjects([this]);
  }

  onDetach(): void {
    if(this.name.value != null)
      this.parent.nameDependency.subtract(this.name.value);
  }

  addUser(from?: ProviderUser, shouldUpdate: boolean = true, forceNew: boolean = false) {
    this.users.push(new ProviderUserGroup(from, this.usersGroup.updators, this.emailDependency, this.seatDependency, this, forceNew));
    if(shouldUpdate)
      this.userStatusField.update();
  }

  removeUser(index: number, shouldUpdate: boolean = true) {
    this.users[index].destroy();
    this.users.splice(index, 1);
    if(shouldUpdate)
      this.userStatusField.update();
  }

  hasSeats(): boolean {
    return this.users.length < this.seatCount;
  }

  static compare(a: SavedProvider, b: SavedProvider): number {
    let comp: number = a.getListingName().toLocaleLowerCase().localeCompare(b.getListingName().toLocaleLowerCase());
    if(comp != 0)
      return comp;
    return (b.id || 0) - (a.id || 0);
  }
}

export class ProviderUserGroup extends SavedGroupStatus {
  firstName: string;
  lastName: string;
  email: SavedTextField;
  userRole: SavedSelectField<ProviderUserRole>;
  active: SavedSelectField<boolean>;
  emailDependency: MapCountDependency<string>;
  seatDependency: SumContributionsDependency;
  isNew: boolean;
  provider: SavedProvider;
  teamRole: SavedSelectField<String>;

  constructor(from: ProviderUser, parentUpdators: CountUpdators, emailDependency: MapCountDependency<string>, seatDependency: SumContributionsDependency, provider?: SavedProvider, forceNew: boolean = false) {
    super(parentUpdators);
    this.isNew = forceNew || from == null;
    this.provider = provider;
    this.emailDependency = emailDependency;
    this.seatDependency = seatDependency;
    this.firstName = from ? from.firstName : null;
    this.lastName = from ? from.lastName : null;
    this.email = new SavedTextField(from ? from.email : null, null, this.updators, [emailDependency],
      [
        (field: SavedTextField): string => field.value?.length > 0 && !Util.isEmail(field.value) ? 'Invalid email' : null,
        (field: SavedTextField): string => emailDependency.count(field.value) > 1 ? 'Duplicate' : null
      ], null, (oldValue: string, newValue: string): void => {
        if(oldValue != null && newValue != null)
          emailDependency.replace(oldValue, newValue);
        else if(oldValue != null)
          emailDependency.subtract(oldValue);
        else if(newValue != null)
          emailDependency.add(newValue);
      }, true, null, 100
    );
    this.active = new SavedSelectField<boolean>(this.getActive(from), null,
      this.updators, null, null, true, null, null, new SelectOptions<boolean>(
        [{ value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }],
        (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)
      ), null, (oldValue: boolean, newValue: boolean): void => {
        if(oldValue != newValue)
          this.setSeatContribution(newValue);
      }
    );
    this.userRole = new SavedSelectField<ProviderUserRole>(from ? from.userRole : null, null, this.updators, null, null, true,
      null, null, new SelectOptions([
        { value: ProviderUserRole.ADMIN, displayValue: 'Admin' },
        { value: ProviderUserRole.EDITOR, displayValue: 'Editor' },
        { value: ProviderUserRole.PUBLISHER, displayValue: 'Publisher' },
        { value: ProviderUserRole.VIEWER, displayValue: 'Viewer' }
      ])
    );

  }

  getActive(from: ProviderUser): boolean {
    return from ? from.providerUserStatus == ProviderUserStatus.ACTIVE : (!this.provider || this.provider.hasSeats());
  }

  setSeatContribution(present: boolean): void {
    this.seatDependency.setContribution(this.email.getDependencyId(), present ? 1 : 0);
  }

  destroy() {
    if(this.email.value != null)
      this.emailDependency.subtract(this.email.value);
    this.setSeatContribution(false);
    this.detach();
  }

  updateFrom(from: ProviderUser) {
    this.isNew = from == null;
    this.firstName = from ? from.firstName : null;
    this.lastName = from ? from.lastName : null;
    this.email.savedValue = from ? from.email : null;
    this.active.savedValue = this.getActive(from);
    this.userRole.savedValue = from ? from.userRole : null;
  }

  touchAll(value: boolean = true) {
    this.email.touch(value);
    this.userRole.touch(value);
  }

  resetAll() {
    this.email.reset();
    this.userRole.reset();
  }

  getSaved(): ProviderUser {
    return new ProviderUser({
      email: this.email.value,
      userRole: this.userRole.value,
      providerUserStatus: this.active.value ? ProviderUserStatus.ACTIVE : ProviderUserStatus.INACTIVE
    });
  }

  shouldShowNames(): boolean {
    return !(this.isNew || this.email.isDirty());
  }




}
